import React from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-600 hover:text-gray-900 flex items-center"
            >
              ← Back
            </button>
            <div className="text-center">
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent leading-tight">
                The Course Creator's Vault
              </h1>
              <p className="text-[10px] sm:text-xs text-gray-500">by Templimize AI</p>
            </div>
            <div className="w-20"></div>
          </div>
        </div>
      </div>

      {/* Privacy Policy Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-gray-600 mb-8">Last Updated: January 13, 2025</p>

          <div className="space-y-8 text-gray-700">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
              <p>
                Welcome to Templimize AI ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered course creation platform.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">2.1 Information You Provide</h3>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Account Information:</strong> Name, email address, and password when you create an account</li>
                <li><strong>Payment Information:</strong> Billing details processed securely through Stripe (we do not store your full payment card details)</li>
                <li><strong>Course Content:</strong> Course titles, descriptions, modules, lessons, and other content you create</li>
                <li><strong>Communications:</strong> Messages, feedback, and support requests you send to us</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">2.2 Automatically Collected Information</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Usage Data:</strong> Information about how you use our platform, including features accessed and time spent</li>
                <li><strong>Device Information:</strong> Browser type, operating system, IP address, and device identifiers</li>
                <li><strong>Analytics Data:</strong> Performance metrics and user behavior patterns to improve our services</li>
              </ul>
            </section>

            {/* How We Use Your Information */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
              <p className="mb-3">We use your information for the following purposes:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Service Delivery:</strong> To provide, maintain, and improve our AI course creation platform</li>
                <li><strong>Account Management:</strong> To create and manage your account and subscription</li>
                <li><strong>AI Processing:</strong> To generate course content, topics, quizzes, and marketing materials using AI technology</li>
                <li><strong>Payment Processing:</strong> To process transactions and prevent fraud</li>
                <li><strong>Communication:</strong> To send you updates, newsletters, marketing materials, and support responses</li>
                <li><strong>Analytics:</strong> To understand usage patterns and improve user experience</li>
                <li><strong>Legal Compliance:</strong> To comply with legal obligations and protect our rights</li>
              </ul>
            </section>

            {/* AI and Data Processing */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. AI and Data Processing</h2>
              <p className="mb-3">
                Templimize AI uses third-party AI services (including OpenAI) to generate course content. When you use our AI features:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Your prompts and inputs may be sent to AI service providers for processing</li>
                <li>Generated content is stored in your account and may be used to improve our services</li>
                <li>We do not sell or share your course content with third parties for their marketing purposes</li>
                <li>AI-generated content is provided "as-is" and you are responsible for reviewing and editing it</li>
              </ul>
            </section>

            {/* Information Sharing */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Information Sharing and Disclosure</h2>
              <p className="mb-3">We may share your information with:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Service Providers:</strong> Third-party companies that help us operate our platform (e.g., Stripe for payments, Resend for emails, OpenAI for AI processing)</li>
                <li><strong>Legal Requirements:</strong> When required by law, court order, or legal process</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                <li><strong>With Your Consent:</strong> When you explicitly agree to share information</li>
              </ul>
              <p className="mt-3">
                <strong>We do NOT:</strong> Sell your personal information to third parties or share it for their direct marketing purposes.
              </p>
            </section>

            {/* Data Security */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Security</h2>
              <p>
                We implement appropriate technical and organizational security measures to protect your information, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Encryption of data in transit and at rest</li>
                <li>Secure authentication using JWT tokens</li>
                <li>Password hashing with bcrypt</li>
                <li>Regular security audits and updates</li>
                <li>Access controls and admin authentication</li>
              </ul>
              <p className="mt-3">
                However, no method of transmission over the internet is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
              </p>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Your Rights and Choices</h2>
              <p className="mb-3">You have the following rights regarding your personal information:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
                <li><strong>Export:</strong> Export your course content in PDF or HTML format</li>
                <li><strong>Marketing Opt-Out:</strong> Unsubscribe from marketing emails at any time</li>
                <li><strong>Data Portability:</strong> Request your data in a machine-readable format</li>
              </ul>
              <p className="mt-3">
                To exercise these rights, contact us at <a href="mailto:sloane@templimizeai.com" className="text-purple-600 hover:underline">sloane@templimizeai.com</a>
              </p>
            </section>

            {/* Cookies */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Cookies and Tracking</h2>
              <p>
                We use cookies and similar tracking technologies to enhance your experience. These may include:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li><strong>Essential Cookies:</strong> Required for authentication and platform functionality</li>
                <li><strong>Analytics Cookies:</strong> To understand how users interact with our platform</li>
                <li><strong>Preference Cookies:</strong> To remember your settings and preferences</li>
              </ul>
              <p className="mt-3">
                You can control cookies through your browser settings, but disabling them may affect platform functionality.
              </p>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Data Retention</h2>
              <p>
                We retain your information for as long as your account is active or as needed to provide services. After account deletion, we may retain certain information for:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Legal compliance and record-keeping requirements</li>
                <li>Resolving disputes and enforcing agreements</li>
                <li>Backup and disaster recovery purposes (up to 90 days)</li>
              </ul>
            </section>

            {/* Children's Privacy */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Children's Privacy</h2>
              <p>
                Templimize AI is not intended for users under 13 years of age. We do not knowingly collect personal information from children under 13. If you become aware that a child has provided us with personal information, please contact us immediately.
              </p>
            </section>

            {/* International Users */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. International Users</h2>
              <p>
                Our services are hosted in the United States. If you access our platform from outside the US, your information may be transferred to, stored, and processed in the US. By using our services, you consent to this transfer.
              </p>
            </section>

            {/* Third-Party Links */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Third-Party Links and Services</h2>
              <p>
                Our platform may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies.
              </p>
            </section>

            {/* Changes to Policy */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any material changes by:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Posting the updated policy on our platform</li>
                <li>Updating the "Last Updated" date</li>
                <li>Sending an email notification for significant changes</li>
              </ul>
              <p className="mt-3">
                Your continued use of the platform after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            {/* Contact Us */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Contact Us</h2>
              <p className="mb-3">
                If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p><strong>Email:</strong> <a href="mailto:sloane@templimizeai.com" className="text-purple-600 hover:underline">sloane@templimizeai.com</a></p>
                <p className="mt-2"><strong>Website:</strong> <a href="https://templimizeai.com" className="text-purple-600 hover:underline" target="_blank" rel="noopener noreferrer">templimizeai.com</a></p>
                <p className="mt-2"><strong>Business Name:</strong> Templimize AI</p>
              </div>
            </section>

            {/* GDPR/CCPA Notice */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Additional Rights for EU and California Residents</h2>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">For EU Residents (GDPR)</h3>
              <p>Under the GDPR, you have additional rights including the right to object to processing, restrict processing, and lodge a complaint with a supervisory authority.</p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">For California Residents (CCPA)</h3>
              <p>Under the CCPA, you have the right to know what personal information we collect, delete your information, and opt-out of the sale of your information (note: we do not sell personal information).</p>
            </section>

            {/* Agreement */}
            <section className="mt-8 p-6 bg-purple-50 rounded-lg border-2 border-purple-200">
              <p className="text-gray-900 font-semibold mb-2">By using Templimize AI, you acknowledge that you have read and understood this Privacy Policy and agree to its terms.</p>
              <p className="text-gray-700 text-sm">
                If you do not agree with this policy, please do not use our services.
              </p>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
